import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getDeviceId } from '@/lib/deviceHeartbeat';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ChefHat, Clock, Check, Bell, RefreshCw, UtensilsCrossed,
  Users, Coffee, LogOut, Volume2, VolumeX, Timer, Armchair, Info, Mic, Boxes,
  Lock, Unlock, Route,
} from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { resolveItemRoute } from '@/lib/printRouting';
import { useWebSocket } from '@/hooks/useWebSocket';
import { StockRequestPopup } from '@/components/stock/StockRequestPopup';
import { ChefStockRequests } from '@/components/stock/ChefStockRequests';
import { ScheduledBadge } from '@/components/cashier-pos/ScheduledServiceField';
import { OlderKitchenOrders } from '@/components/kitchen/OlderKitchenOrders';

const ORDER_STATUS_COLORS = {
  preparing: 'bg-warning/20 border-warning/30 text-warning',
  ready: 'bg-success/20 border-success/30 text-success',
};

// Suggested default station names (user can add more per branch).
const DEFAULT_STATIONS = ['Kitchen', 'Bar', 'Grill', 'Drinks', 'Dessert'];

// Web-Audio distinct beep per station. Frequency is derived from the station
// name so every station (incl. custom ones) gets a stable, distinct tone.
let _audioCtx = null;
const _stationFreq = (label) => {
  const base = { kitchen: 660, bar: 440, grill: 784, drinks: 523, dessert: 880 };
  const key = (label || '').toLowerCase();
  for (const k in base) { if (key.includes(k)) return base[k]; }
  // hash fallback → 380–900 Hz
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 520;
  return 380 + h;
};
// Optional spoken call-out ("New order — Bar") via the browser voice.
const speakStation = (label) => {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const clean = (label || 'kitchen').replace(/—/g, ' ');
    const u = new SpeechSynthesisUtterance(`New order. ${clean}`);
    u.rate = 1; u.volume = 1; u.pitch = 1;
    synth.cancel();
    synth.speak(u);
  } catch (e) { /* non-critical */ }
};
const playStationTone = (label) => {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    const freq = _stationFreq(label);
    // two short pulses so it reads as a "new order" chime, not a system blip
    [0, 0.18].forEach((offset) => {
      const osc = _audioCtx.createOscillator();
      const gain = _audioCtx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      osc.connect(gain); gain.connect(_audioCtx.destination);
      const t = _audioCtx.currentTime + offset;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.4, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
      osc.start(t); osc.stop(t + 0.15);
    });
  } catch (e) { /* non-critical */ }
};

export default function KitchenDisplayPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const [stationFilter, setStationFilter] = useState('all');
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [customStations, setCustomStations] = useState([]);
  const [newStationName, setNewStationName] = useState('');
  // Phase C — per-device station lock (localStorage, scoped per branch) so a
  // KDS tablet stays on its station during a shift. Reuses the existing
  // stationFilter; no device-management infra.
  const [stationLocked, setStationLocked] = useState(false);
  const [showRoutingMap, setShowRoutingMap] = useState(false);
  const lastCountsByStationRef = useRef({});
  const hasLoadedOnceRef = useRef(false);
  const [pin, setPin] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [branchFilter, setBranchFilter] = useState('');
  const [kdsView, setKdsView] = useState('orders');  // iter940 — 'orders' | 'stock'
  // Realtime — reuse the existing ws:dashboard bus so fired/updated checks
  // refresh instantly. The 5s poll below stays purely as a recovery fallback.
  const wsRefreshRef = useRef(null);
  useWebSocket(useCallback((msg) => {
    const type = msg?.type || msg?.event;
    if (['check_items_added', 'check_opened', 'check_completed', 'new_sale', 'check_items_cancelled', 'check_voided'].includes(type)) {
      if (wsRefreshRef.current) wsRefreshRef.current();
    }
  }, []));
  // iter658 — Smart Routing on KDS: cache categories + printers so we can
  // resolve which station each line item came from. Tiny payload, cached
  // for the session — refreshed only when the cashier saves a new printer.
  const [categories, setCategories] = useState([]);
  const [printers, setPrinters] = useState([]);
  const { branches, fetchBranches: _fetchBr } = useBranchStore();

  // Simple PIN authentication for kitchen. Primary path reuses the EXISTING
  // cashier PIN login (same vetted endpoint Waiter/POS use) so a real cashier/
  // manager PIN mints a proper token. The legacy 1234/0000 default is kept as
  // a fallback for existing kitchen tablets, but we NEVER mark the screen
  // authenticated unless a real token was actually obtained.
  const handlePinLogin = async () => {
    const entered = (pin || '').trim();
    if (entered.length < 4) { toast.error('Enter your PIN'); return; }
    const activate = (token) => {
      localStorage.setItem('cashier_token', token);
      localStorage.setItem('kitchen_auth', 'true');
      setIsAuthenticated(true);
      toast.success('Kitchen Display Active');
    };
    // 1) Real cashier/manager PIN (preferred — no hardcoded credentials).
    try {
      const { data } = await api.post('/cashier/login', { pin: entered, device_id: getDeviceId() });
      if (data?.access_token) { activate(data.access_token); return; }
    } catch { /* fall through to legacy quick-access */ }
    // 2) Legacy quick-access default (kept for existing kitchen tablets).
    if (entered === '1234' || entered === '0000') {
      try {
        const { data } = await api.post('/cashier/login', {
          email: 'ss@ssc.com', password: 'Aa147258369Ssc@',
        });
        if (data?.access_token) { activate(data.access_token); return; }
      } catch { /* ignore — surfaced below */ }
    }
    toast.error('Invalid PIN — enter your cashier PIN');
  };

  // Check existing auth
  useEffect(() => {
    const auth = localStorage.getItem('kitchen_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Resolve the dominant prep-station label for an order (reuses the same
  // routing logic the per-order banner uses). Returns e.g. "Kitchen",
  // "Bar", "Kitchen — Grill", or null when unrouted.
  const getOrderStation = useCallback((order) => {
    const routes = (order.items || []).map((it) =>
      resolveItemRoute({ categoryId: it.category, categories, printers, branchId: order.branch_id }));
    const tally = {};
    routes.forEach((r) => {
      if (r.role === 'none' || r.role === 'unknown') return;
      const key = `${r.role}|${r.station || ''}`;
      tally[key] = (tally[key] || 0) + 1;
    });
    const top = Object.entries(tally).sort((a, b) => b[1] - a[1])[0];
    if (!top) return null;
    const [role, station] = top[0].split('|');
    const roleLabel = role === 'kitchen' ? 'Kitchen' : (role === 'bar' ? 'Bar' : role);
    return station ? `${roleLabel} — ${station}` : roleLabel;
  }, [categories, printers]);

  // Phase C — per-ITEM station label so a station screen can show only the
  // lines routed to it (e.g. Grill sees the burger, Bar sees the cola) while
  // both remain on the same canonical order. Reuses resolveItemRoute.
  const itemStationLabel = useCallback((order, item) => {
    const r = resolveItemRoute({ categoryId: item.category, categories, printers, branchId: order.branch_id });
    if (r.role === 'none' || r.role === 'unknown') return null;
    const roleLabel = r.role === 'kitchen' ? 'Kitchen' : 'Bar';
    return r.station ? `${roleLabel} — ${r.station}` : roleLabel;
  }, [categories, printers]);

  const orderMatchesStation = useCallback((order, station) => {
    if (!station || station === 'all') return true;
    const s = station.toLowerCase();
    return (order.items || []).some((it) => {
      const label = itemStationLabel(order, it);
      return !!label && label.toLowerCase().includes(s);
    });
  }, [itemStationLabel]);

  // Load persisted per-branch station settings.
  useEffect(() => {
    const bk = branchFilter || 'all';
    try {
      setStationFilter(localStorage.getItem(`kds.station.${bk}`) || 'all');
      setCustomStations(JSON.parse(localStorage.getItem(`kds.customStations.${bk}`) || '[]'));
      setVoiceEnabled(localStorage.getItem(`kds.voice.${bk}`) === '1');
      setStationLocked(localStorage.getItem(`kds.stationLock.${bk}`) === '1');
    } catch { setStationFilter('all'); setCustomStations([]); setStationLocked(false); }
    lastCountsByStationRef.current = {};
    hasLoadedOnceRef.current = false;
  }, [branchFilter]);

  // Phase C — stations actually CONFIGURED for the selected branch, derived
  // from the already-fetched printers (mirrors the backend routing-options
  // rule). These become the station picker options so a KDS only offers the
  // real prep lanes for that branch. Falls back to detected/defaults when no
  // printers are configured yet.
  const configuredStations = useMemo(() => {
    const list = (printers || []).filter((p) =>
      p.is_active !== false &&
      ['kitchen', 'bar'].includes((p.type || '').toLowerCase()) &&
      (!branchFilter || !p.branch_id || p.branch_id === branchFilter));
    let hasKitchen = false, hasBar = false;
    const stations = [];
    list.forEach((p) => {
      const role = (p.type || '').toLowerCase();
      const station = (p.station || '').trim();
      if (role === 'kitchen') hasKitchen = true;
      if (role === 'bar') hasBar = true;
      if (station && !stations.includes(station)) stations.push(station);
    });
    const out = [];
    if (hasKitchen) out.push('Kitchen');
    if (hasBar) out.push('Bar');
    stations.forEach((s) => { if (!out.includes(s)) out.push(s); });
    return out;
  }, [printers, branchFilter]);

  const branchName = useMemo(() => {
    if (!branchFilter) return 'All Branches';
    return branches.find((b) => b.id === branchFilter)?.name || 'Branch';
  }, [branchFilter, branches]);

  const toggleStationLock = () => {
    const bk = branchFilter || 'all';
    if (!stationLocked && stationFilter === 'all') {
      toast.info('Pick a station first, then lock this screen to it');
      return;
    }
    const next = !stationLocked;
    setStationLocked(next);
    try { localStorage.setItem(`kds.stationLock.${bk}`, next ? '1' : '0'); } catch { /* ignore */ }
    toast.success(next ? `Locked to "${stationFilter}" on this device` : 'Station unlocked');
  };

  const persistStationFilter = (v) => {
    setStationFilter(v);
    try { localStorage.setItem(`kds.station.${branchFilter || 'all'}`, v); } catch { /* ignore */ }
  };
  const addCustomStation = () => {
    const n = (newStationName || '').trim();
    if (!n) return;
    const next = Array.from(new Set([...customStations, n]));
    setCustomStations(next);
    setNewStationName('');
    try { localStorage.setItem(`kds.customStations.${branchFilter || 'all'}`, JSON.stringify(next)); } catch { /* ignore */ }
  };

  // Fetch orders — ONE canonical kitchen rail. The backend returns every
  // order whose effective kitchen state (kitchen_status, else legacy status)
  // is preparing/ready, including OPEN waiter checks that have fired items.
  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };

      const params = new URLSearchParams();
      params.append('kitchen', '1');
      if (branchFilter) params.append('branch_id', branchFilter);

      const res = await api.get(`/cashier/orders?${params.toString()}`, { headers });
      const effStatus = (o) => o.kitchen_status || o.status;
      const allOrders = (res.data || []);
      const sorted = allOrders.sort((a, b) => {
        const sa = effStatus(a), sb = effStatus(b);
        if (sa === 'preparing' && sb === 'ready') return -1;
        if (sa === 'ready' && sb === 'preparing') return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });

      // Per-station new-order detection → distinct sound per station.
      const preparing = allOrders.filter((o) => effStatus(o) === 'preparing');
      const counts = {};
      preparing.forEach((o) => {
        const label = getOrderStation(o) || 'Unrouted';
        counts[label] = (counts[label] || 0) + 1;
      });
      if (soundEnabled && hasLoadedOnceRef.current) {
        const prev = lastCountsByStationRef.current || {};
        Object.keys(counts).forEach((label) => {
          if (counts[label] > (prev[label] || 0)) {
            if (stationFilter === 'all' || label.toLowerCase().includes(stationFilter.toLowerCase())) {
              playStationTone(label);
              if (voiceEnabled) speakStation(label);
            }
          }
        });
      }
      lastCountsByStationRef.current = counts;
      hasLoadedOnceRef.current = true;
      setLastOrderCount(preparing.length);
      setOrders(sorted);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setLoading(false);
    }
  }, [branchFilter, soundEnabled, stationFilter, voiceEnabled, getOrderStation]);

  // Fetch branches
  const fetchBranches = useCallback(async () => {
    try {
      await _fetchBr();
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    }
  }, []);

  // iter658 — Fetch the routing-resolution inputs (categories + printers)
  // exactly once on mount. Both lists are tiny and rarely change during a
  // shift, so we skip auto-refresh to keep KDS noise-free.
  const fetchRoutingData = useCallback(async () => {
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const [catsRes, prsRes] = await Promise.all([
        api.get('/cashier/categories', { headers }).catch(() => ({ data: [] })),
        api.get('/cashier/printers', { headers }).catch(() => ({ data: [] })),
      ]);
      setCategories(catsRes.data || []);
      setPrinters(prsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch routing data:', err);
    }
  }, []);

  // Recovery poll (realtime WS above handles the common case). Also keep the
  // ref pointing at the latest fetchOrders so WS events refetch correctly.
  useEffect(() => {
    if (!isAuthenticated) return;
    wsRefreshRef.current = fetchOrders;
    fetchOrders();
    fetchBranches();
    fetchRoutingData();
    const interval = setInterval(fetchOrders, 8000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchOrders, fetchBranches, fetchRoutingData]);

  // Mark order items ready — sets kitchen_status ONLY (Ready ≠ Paid). The
  // business status of a waiter check stays 'open' for the cashier.
  // Phase C — station-aware helpers. When a specific station is selected the
  // Ready/Served action targets ONLY that station (backend tracks
  // ready_stations/served_stations); on "All stations" it's the legacy
  // whole-order action.
  const stationIn = (order, field, token) =>
    (order?.[field] || []).some((t) => String(t).toLowerCase() === String(token).toLowerCase());

  const markAsReady = async (orderId, station = null) => {
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const body = { kitchen_status: 'ready', ...(station ? { station } : {}) };
      await api.put(`/cashier/orders/${orderId}/status`, body, { headers });
      toast.success(station ? `${station} marked ready!` : 'Order marked as ready!');
      fetchOrders();
    } catch (err) {
      toast.error('Failed to update order');
    }
  };

  // Mark served — clears the ticket off THIS station's KDS (or the whole
  // order on "All stations"). Does NOT finalise/pay the check; the cashier
  // does that via the POS.
  const markAsCompleted = async (orderId, station = null) => {
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const body = { kitchen_status: 'served', ...(station ? { station } : {}) };
      await api.put(`/cashier/orders/${orderId}/status`, body, { headers });
      toast.success(station ? `${station} served!` : 'Order served!');
      fetchOrders();
    } catch (err) {
      toast.error('Failed to update order');
    }
  };

  // Calculate time elapsed
  const getTimeElapsed = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m ago`;
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('kitchen_auth');
    setIsAuthenticated(false);
    setPin('');
  };

  // PIN Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-800 via-stone-700 to-stone-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm shadow-2xl border-0 bg-white/95" data-testid="kitchen-login-card">
          <CardContent className="pt-8 pb-6 px-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-warning/20 rounded-2xl flex items-center justify-center mb-4">
                <ChefHat size={32} className="text-warning" />
              </div>
              <h1 className="text-2xl font-bold font-outfit text-foreground">Kitchen Display</h1>
              <p className="text-sm text-muted-foreground mt-1">Enter PIN to access</p>
            </div>
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Enter your cashier PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePinLogin()}
                className="h-14 text-center text-2xl tracking-widest font-mono"
                maxLength={4}
                data-testid="kitchen-pin"
              />
              <Button 
                onClick={handlePinLogin}
                className="w-full h-12 bg-warning hover:bg-warning text-white font-semibold"
                data-testid="kitchen-login-btn"
              >
                Access Kitchen Display
              </Button>
              <p className="text-xs text-center text-muted-foreground">Use your cashier/manager PIN (default: 1234)</p>
            </div>
            <div className="mt-6 text-center">
              <a href="/cashier" className="text-sm text-warning hover:underline">
                ← Go to Cashier POS
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-foreground text-white" data-testid="kitchen-display">
      {/* Header */}
      <div className="bg-muted border-b border-stone-700 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-warning rounded-xl flex items-center justify-center">
            <ChefHat size={28} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-outfit">Kitchen Display</h1>
            {/* Phase C — make the active Branch + Station obvious on every
                tablet so staff always know what this screen is showing. */}
            <div className="flex items-center gap-2 text-sm mt-0.5 flex-wrap" data-testid="kds-context-banner">
              <span className="text-muted-foreground">Branch:</span>
              <span className="font-semibold text-white" data-testid="kds-active-branch">{branchName}</span>
              <span className="text-stone-600">|</span>
              <span className="text-muted-foreground">Station:</span>
              <span className="font-semibold text-amber-400" data-testid="kds-active-station">
                {stationFilter === 'all' ? 'All stations' : stationFilter}
              </span>
              {stationLocked && (
                <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px]" data-testid="kds-lock-badge">
                  <Lock size={10} className="mr-1" /> Locked
                </Badge>
              )}
            </div>
          </div>
          {/* iter700 — Quick "How does this connect?" tooltip for new
              kitchen staff so they understand the auto-refresh + branch
              flow without needing an admin to explain it. */}
          <details className="relative ml-2">
            <summary className="list-none cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-full bg-stone-700 hover:bg-stone-600 text-amber-300" data-testid="kds-help-trigger" title="How this connects">
              <Info size={14} />
            </summary>
            <div className="absolute top-full left-0 mt-1 w-80 bg-stone-900 border border-stone-700 rounded-xl shadow-xl p-3 z-30 text-xs leading-relaxed text-muted-foreground space-y-1.5" data-testid="kds-help-content">
              <p className="font-semibold text-white">Kitchen Display setup</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Open <code className="bg-stone-800 px-1 rounded">{`{your-domain}/kds`}</code> on a kitchen tablet — log in with the cashier PIN.</li>
                <li>Orders auto-refresh every 5 seconds; new tickets play a chime (toggle the bell icon to mute).</li>
                <li>Filter by branch above when one kitchen handles multiple branches.</li>
                <li>Tap <strong>Preparing → Ready</strong> on each ticket; waiters at the Waiter screen and cashiers in their Orders dialog see the status flip instantly.</li>
              </ol>
            </div>
          </details>
        </div>
        <div className="flex items-center gap-4 overflow-x-auto min-w-0 [&>*]:shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {/* Branch Filter */}
          <select 
            value={branchFilter} 
            onChange={(e) => setBranchFilter(e.target.value)}
            className="bg-stone-700 border-stone-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Branches</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>

          {/* iter940 — Orders / Stock Requests tab toggle */}
          <div className="flex items-center bg-stone-700 rounded-lg p-0.5" data-testid="kds-view-tabs">
            <button onClick={() => setKdsView('orders')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${kdsView === 'orders' ? 'bg-amber-500 text-white' : 'text-stone-300'}`}
              data-testid="kds-tab-orders">Orders</button>
            <button onClick={() => setKdsView('stock')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1 ${kdsView === 'stock' ? 'bg-amber-500 text-white' : 'text-stone-300'}`}
              data-testid="kds-tab-stock"><Boxes size={14} /> Stock Requests</button>
          </div>

          {/* This-screen station selector — sound + filter are scoped to it.
              Phase C: options come from the branch's CONFIGURED prep stations
              (printers). A per-device Lock keeps it fixed during a shift. */}
          {(() => {
            const detected = Array.from(new Set(orders.map(o => getOrderStation(o)).filter(Boolean)));
            const all = configuredStations.length
              ? Array.from(new Set([...configuredStations, ...customStations]))
              : Array.from(new Set([...detected, ...DEFAULT_STATIONS, ...customStations]));
            return (
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">This screen</span>
                <select
                  value={stationFilter}
                  onChange={(e) => persistStationFilter(e.target.value)}
                  disabled={stationLocked}
                  className={`bg-stone-700 border-stone-600 text-white rounded-lg px-3 py-2 text-sm ${stationLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid="kds-station-select"
                >
                  <option value="all">All stations</option>
                  {all.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <Button
                  variant="outline" size="sm"
                  onClick={toggleStationLock}
                  className={`border-stone-600 text-xs ${stationLocked ? 'text-amber-400 border-amber-400' : 'text-muted-foreground'}`}
                  data-testid="kds-station-lock-btn"
                  title={stationLocked ? 'Unlock to change station' : 'Lock this screen to the selected station'}
                >
                  {stationLocked ? <><Unlock size={14} className="mr-1" />Change Station</> : <><Lock size={14} className="mr-1" />Lock</>}
                </Button>
                {!stationLocked && (
                  <input
                    value={newStationName}
                    onChange={(e) => setNewStationName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustomStation()}
                    placeholder="+ add station"
                    className="bg-stone-700 border border-stone-600 text-white rounded-lg px-2 py-2 text-xs w-28 placeholder:text-stone-400"
                    data-testid="kds-add-station-input"
                  />
                )}
                <Button variant="outline" size="sm" onClick={() => setShowRoutingMap(true)}
                  className="border-stone-600 text-muted-foreground text-xs" data-testid="kds-routing-map-btn"
                  title="See which category prints to which station">
                  <Route size={14} className="mr-1" />Routing map
                </Button>
                <Button variant="outline" size="sm" onClick={() => playStationTone(stationFilter === 'all' ? 'Kitchen' : stationFilter)}
                  className="border-stone-600 text-muted-foreground text-xs" data-testid="kds-test-sound-btn">
                  Test sound
                </Button>
              </div>
            );
          })()}

          {/* Order Count */}
          <div className="flex items-center gap-2 bg-warning/20 px-4 py-2 rounded-xl">
            <Bell size={20} className="text-amber-400" />
            <span className="text-2xl font-bold text-amber-400" data-testid="order-count">{orders.length}</span>
            <span className="text-sm text-amber-300">Orders</span>
          </div>
          
          <OlderKitchenOrders branchId={branchFilter} />

          {/* Sound Toggle */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="border-stone-600 text-muted-foreground"
            data-testid="sound-toggle"
          >
            {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </Button>

          {/* Voice call-out toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const v = !voiceEnabled; setVoiceEnabled(v);
              try { localStorage.setItem(`kds.voice.${branchFilter || 'all'}`, v ? '1' : '0'); } catch { /* ignore */ }
              if (v) speakStation(stationFilter === 'all' ? 'Kitchen' : stationFilter);
            }}
            className={`border-stone-600 text-xs ${voiceEnabled ? 'text-amber-400 border-amber-400' : 'text-muted-foreground'}`}
            data-testid="kds-voice-toggle"
          >
            <Mic size={16} className="mr-1.5" />Voice {voiceEnabled ? 'ON' : 'OFF'}
          </Button>
          
          {/* Refresh */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={fetchOrders}
            className="border-stone-600 text-muted-foreground"
            data-testid="refresh-btn"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </Button>
          
          {/* Logout */}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-white"
            data-testid="kitchen-logout"
          >
            <LogOut size={20} />
          </Button>
        </div>
      </div>

      {/* Orders Grid */}
      {kdsView === 'stock' ? (
        <ChefStockRequests branchId={branchFilter} />
      ) : (
      <div className="p-6">
        {(() => { const perStation = stationFilter !== 'all'; const visibleOrders = !perStation ? orders : orders.filter(o => orderMatchesStation(o, stationFilter) && !stationIn(o, 'served_stations', stationFilter)); return (
        visibleOrders.length === 0 ? (
          <div className="text-center py-20">
            <ChefHat size={80} className="mx-auto text-foreground mb-4" />
            <h2 className="text-2xl font-bold text-muted-foreground">No Orders</h2>
            <p className="text-muted-foreground mt-2">{stationFilter === 'all' ? 'Waiting for new orders...' : `Waiting for new "${stationFilter}" orders...`}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {visibleOrders.map(order => {
              // Phase C — when viewing a specific station, the card's status
              // reflects THAT station's state (ready_stations/served_stations);
              // on "All stations" it uses the derived overall kitchen_status.
              const kStatus = perStation
                ? (stationIn(order, 'served_stations', stationFilter) ? 'served'
                   : stationIn(order, 'ready_stations', stationFilter) ? 'ready' : 'preparing')
                : (order.kitchen_status || order.status);
              // Phase C — when a specific station is selected, show only that
              // station's lines; the order still belongs to the same canonical
              // pos_order (all items untouched in the DB).
              const displayItems = stationFilter === 'all'
                ? (order.items || [])
                : (order.items || []).filter((it) => {
                    const l = itemStationLabel(order, it);
                    return !!l && l.toLowerCase().includes(stationFilter.toLowerCase());
                  });
              const maxRound = Math.max(0, ...(order.items || []).map((i) => i.fire_round || 0));
              const hasIncremental = (order.fire_count || 0) > 1;
              const timeElapsed = getTimeElapsed(order.created_at);
              const isUrgent = new Date() - new Date(order.created_at) > 10 * 60 * 1000; // 10 mins
              // iter658 — Resolve per-line route + dominant station for banner.
              const itemRoutes = displayItems.map((it) =>
                resolveItemRoute({
                  categoryId: it.category,
                  categories,
                  printers,
                  branchId: order.branch_id,
                })
              );
              // Dominant route = the label most lines share (skip 'none').
              const dominant = (() => {
                const tally = {};
                itemRoutes.forEach((r) => {
                  if (r.role === 'none' || r.role === 'unknown') return;
                  const key = `${r.role}|${r.station || ''}`;
                  tally[key] = (tally[key] || 0) + 1;
                });
                const top = Object.entries(tally).sort((a, b) => b[1] - a[1])[0];
                if (!top) return null;
                const [key] = top;
                const [role, station] = key.split('|');
                return { role, station: station || null };
              })();
              const dominantLabel = dominant
                ? (dominant.station
                    ? `${dominant.role === 'kitchen' ? 'Kitchen' : 'Bar'} — ${dominant.station}`
                    : (dominant.role === 'kitchen' ? 'Kitchen' : 'Bar'))
                : null;
              const dominantTone = dominant?.role === 'bar' ? 'bg-indigo-600' : 'bg-orange-600';

              return (
                <Card 
                  key={order.id}
                  className={`border-2 ${isUrgent ? 'border-error bg-red-950' : 'border-warning bg-muted'}`}
                  data-testid={`order-${order.id}`}
                >
                  <CardContent className="p-4">
                    {/* iter658 — Station banner: shows which prep station this
                        ticket primarily belongs to (helpful when multiple
                        bridges fan-out the same order to kitchen + bar). */}
                    {dominantLabel && (
                      <div
                        className={`${dominantTone} -mx-4 -mt-4 mb-2 px-4 py-1.5 flex items-center justify-between text-white`}
                        data-testid={`kds-station-banner-${order.order_number}`}
                      >
                        <div className="flex items-center gap-2">
                          {dominant.role === 'bar' ? <Coffee size={14} /> : <ChefHat size={14} />}
                          <span className="text-xs font-bold uppercase tracking-wider">{dominantLabel}</span>
                        </div>
                        <span className="text-[10px] opacity-80">SMART ROUTING</span>
                      </div>
                    )}
                    {/* Table Number Banner - Large & Prominent */}
                    {order.table_number && order.order_type === 'dine_in' && (
                      <div className="bg-orange-500 -mx-4 -mt-4 mb-3 px-4 py-3 rounded-t-lg flex items-center justify-between" data-testid={`kds-table-badge-${order.order_number}`}>
                        <div className="flex items-center gap-2">
                          <Armchair size={22} className="text-white" />
                          <span className="text-2xl font-black font-outfit text-white tracking-wide">TABLE {order.table_number}</span>
                        </div>
                        <Badge className="bg-white/20 text-white text-xs">Dine In</Badge>
                      </div>
                    )}

                    {/* Order Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold font-outfit text-amber-400">
                          #{order.order_number}
                        </span>
                        {order.check_label && (
                          <Badge className="bg-orange-600 text-white" data-testid={`kds-check-label-${order.order_number}`}>
                            {order.check_label}
                          </Badge>
                        )}
                        <ScheduledBadge iso={order.scheduled_for} testId={`kds-scheduled-${order.order_number}`} />
                        {isUrgent && (
                          <Badge className="bg-error animate-pulse">URGENT</Badge>
                        )}
                      </div>
                      <Badge className={ORDER_STATUS_COLORS[kStatus] || 'bg-stone-600'}>
                        {kStatus}
                      </Badge>
                    </div>
                    
                    {/* Order Type & Time */}
                    <div className="flex items-center justify-between text-sm mb-3 pb-3 border-b border-stone-700">
                      <div className="flex items-center gap-2">
                        {order.order_type !== 'dine_in' && (
                          <Badge variant="outline" className="text-xs border-stone-600 text-muted-foreground capitalize">
                            {order.order_type?.replace('_', ' ')}
                          </Badge>
                        )}
                        {order.order_type !== 'dine_in' && order.table_number && (
                          <span className="text-muted-foreground">Table {order.table_number}</span>
                        )}
                        {order.customer_name && (
                          <span className="text-muted-foreground text-xs">{order.customer_name}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Timer size={14} />
                        <span className={isUrgent ? 'text-red-400 font-bold' : ''}>{timeElapsed}</span>
                      </div>
                    </div>
                    
                    {/* Order Items */}
                    <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                      {displayItems.map((item, idx) => {
                        const r = itemRoutes[idx];
                        const showChip = r && r.role !== 'none' && r.role !== 'unknown';
                        return (
                        <div key={idx} className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="w-6 h-6 bg-warning/30 rounded-full flex items-center justify-center text-xs font-bold text-amber-400">
                                {item.quantity}
                              </span>
                              <span className="font-medium text-white">{item.name}</span>
                              {hasIncremental && (item.fire_round || 0) === maxRound && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500 text-white animate-pulse"
                                  data-testid={`kds-item-new-${order.order_number}-${idx}`}>
                                  NEW
                                </span>
                              )}
                              {showChip && (
                                <span
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                    r.warning
                                      ? 'bg-amber-500/20 text-amber-200 border border-amber-500/40'
                                      : r.role === 'bar'
                                        ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-500/40'
                                        : 'bg-orange-500/20 text-orange-200 border border-orange-500/40'
                                  }`}
                                  data-testid={`kds-item-route-${order.order_number}-${idx}`}
                                  title={r.label}
                                >
                                  {r.role === 'bar' ? <Coffee size={10} /> : <ChefHat size={10} />}
                                  {r.station || (r.role === 'bar' ? 'Bar' : 'Kitchen')}
                                </span>
                              )}
                            </div>
                            {item.modifiers?.length > 0 && (
                              <div className="ml-8 mt-1 text-xs text-muted-foreground">
                                {item.modifiers.map((m, mi) => (
                                  <span key={mi} className="mr-2">+{m.name}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                      {(order.cancelled_items || []).map((c, ci) => (
                        <div key={`x${ci}`} className="flex items-center gap-2 text-sm text-red-300"
                          data-testid={`kds-item-cancelled-${order.order_number}-${ci}`}>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white">CANCELLED</span>
                          <span className="line-through">{c.quantity} × {c.name}</span>
                          {c.reason && <span className="text-xs text-red-200/70">— {c.reason}</span>}
                        </div>
                      ))}
                    </div>
                    
                    {/* Kitchen Notes */}
                    {order.kitchen_notes && (
                      <div className="bg-warning/10 border border-warning/30 rounded-lg p-2 mb-3">
                        <p className="text-xs text-amber-300 font-medium">Kitchen Notes:</p>
                        <p className="text-sm text-amber-100">{order.kitchen_notes}</p>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      {kStatus === 'preparing' && (
                        <Button 
                          className="flex-1 bg-success hover:bg-success text-white h-12 text-base font-bold"
                          onClick={() => markAsReady(order.id, perStation ? stationFilter : null)}
                          data-testid={`ready-${order.id}`}
                        >
                          <Check size={20} className="mr-2" />
                          Ready{perStation ? ` · ${stationFilter}` : ''}
                        </Button>
                      )}
                      {kStatus === 'ready' && (
                        <Button 
                          className="flex-1 bg-info hover:bg-info text-white h-12 text-base font-bold"
                          onClick={() => markAsCompleted(order.id, perStation ? stationFilter : null)}
                          data-testid={`complete-${order.id}`}
                        >
                          <UtensilsCrossed size={20} className="mr-2" />
                          Served{perStation ? ` · ${stationFilter}` : ''}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )); })()}
      </div>
      )}

      {/* iter940 — Live incoming stock-request popup for the chef's branch. */}
      <StockRequestPopup branchId={branchFilter} />

      {/* Phase C — read-only Category → Station routing map. Reuses the
          already-fetched categories + printers + resolveItemRoute. Helps
          staff understand where each category fires without opening Settings. */}
      {showRoutingMap && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowRoutingMap(false)} data-testid="kds-routing-map-overlay">
          <div className="bg-stone-900 border border-stone-700 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-stone-700">
              <div className="flex items-center gap-2">
                <Route size={18} className="text-amber-400" />
                <h2 className="text-lg font-bold text-white font-outfit">Routing map — {branchName}</h2>
              </div>
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-white" onClick={() => setShowRoutingMap(false)} data-testid="kds-routing-map-close">
                <LogOut size={16} className="rotate-180" />
              </Button>
            </div>
            <p className="px-4 pt-3 text-xs text-muted-foreground">Which KDS station each menu category prints to. Set this under Menu → Manage Categories.</p>
            <div className="p-4 overflow-y-auto space-y-1.5">
              {categories.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">No categories configured yet.</p>
              )}
              {categories.map((c) => {
                const r = resolveItemRoute({ categoryId: c.id, categories, printers, branchId: branchFilter || null });
                const dest = r.role === 'none' ? 'Receipt only'
                  : r.station ? `${r.role === 'bar' ? 'Bar' : 'Kitchen'} — ${r.station}`
                  : (r.role === 'bar' ? 'Bar' : r.role === 'kitchen' ? 'Kitchen' : 'Unrouted');
                const tone = r.warning ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
                  : r.role === 'bar' ? 'bg-indigo-500/20 text-indigo-200 border-indigo-500/40'
                  : r.role === 'none' ? 'bg-stone-700 text-stone-300 border-stone-600'
                  : 'bg-orange-500/20 text-orange-200 border-orange-500/40';
                return (
                  <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-stone-800/60 rounded-lg" data-testid={`kds-routing-row-${c.id}`}>
                    <span className="text-sm text-white font-medium truncate">{c.name}</span>
                    <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${tone}`}>
                      {r.role === 'bar' ? <Coffee size={11} /> : r.role === 'none' ? null : <ChefHat size={11} />}
                      {dest}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-muted border-t border-stone-700 p-3 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>Auto-refresh: 5 seconds</span>
          <span>•</span>
          <span>Sound: {soundEnabled ? 'ON' : 'OFF'}</span>
        </div>
        <div className="text-sm text-muted-foreground">
          Kitchen Display System v1.0
        </div>
      </div>
    </div>
  );
}
